<template>
  <div
    class="chart-gauge-semi-circle"
    :style="{
      '--thickness': `${thickness}px`,
      maxWidth: `${viewBoxWidth}px`,
    }"
  >
    <svg
      :viewBox="`0 0 ${viewBoxWidth} ${viewBoxHeight + 10}`"
      preserveAspectRatio="xMidYMid meet"
      class="gauge-svg"
    >
      <g :transform="`translate(${viewBoxWidth / 2}, ${viewBoxHeight + 10})`">
        <!-- Tracks -->
        <path
          :d="describeArc(0, 0, outerRx, outerRy, 270, 450)"
          class="track"
        />
        <path
          v-if="innerArc"
          :d="describeArc(0, 0, innerRx, innerRy, 270, 450)"
          class="track"
        />

        <!-- Markers -->
        <g class="scale-markers">
          <g
            v-for="marker in markers"
            :key="marker.percentage"
          >
            <line
              :x1="marker.line.x1"
              :y1="marker.line.y1"
              :x2="marker.line.x2"
              :y2="marker.line.y2"
            />
            <text
              :x="marker.label.x"
              :y="marker.label.y"
              class="scale-label scale-label-small"
            >
              {{ marker.percentage }}%
            </text>
          </g>
        </g>

        <!-- Outer arc -->
        <g
          class="arc-group outer"
          @mouseenter="showTooltip('outerTooltipAnchor', outerArc.tooltip)"
          @mouseleave="cleanupTooltips"
        >
          <path
            :d="describeArc(0, 0, outerRx, outerRy, 270, 450)"
            class="hit-path"
          />
          <path
            v-if="outerArc.value > 0"
            :d="describeArc(0, 0, outerRx, outerRy, 270, getEndAngle(outerArc.value, outerCorrection))"
            class="arc-value"
          />
          <circle
            v-if="shouldShowCap(outerArc.value)"
            :cx="getCapPos(outerRx, outerRy, outerArc.value, outerCorrection).x"
            :cy="getCapPos(outerRx, outerRy, outerArc.value, outerCorrection).y"
            :r="thickness / 2"
            class="cap"
          />
          <circle
            ref="outerTooltipAnchor"
            :cx="getTooltipAnchorPos(outerRx, outerRy).x"
            :cy="getTooltipAnchorPos(outerRx, outerRy).y"
            r="1"
            class="tooltip-anchor"
          />
        </g>

        <!-- Inner arc (only when two arcs provided) -->
        <g
          v-if="innerArc"
          class="arc-group inner"
          @mouseenter="showTooltip('innerTooltipAnchor', innerArc.tooltip)"
          @mouseleave="cleanupTooltips"
        >
          <path
            :d="describeArc(0, 0, innerRx, innerRy, 270, 450)"
            class="hit-path"
          />
          <path
            v-if="innerArc.value > 0"
            :d="describeArc(0, 0, innerRx, innerRy, 270, getEndAngle(innerArc.value, innerCorrection))"
            class="arc-value"
          />
          <circle
            v-if="shouldShowCap(innerArc.value)"
            :cx="getCapPos(innerRx, innerRy, innerArc.value, innerCorrection).x"
            :cy="getCapPos(innerRx, innerRy, innerArc.value, innerCorrection).y"
            :r="thickness / 2"
            class="cap"
          />
          <circle
            ref="innerTooltipAnchor"
            :cx="getTooltipAnchorPos(innerRx, innerRy).x"
            :cy="getTooltipAnchorPos(innerRx, innerRy).y"
            r="1"
            class="tooltip-anchor"
          />
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import D3Tooltip from '~/utils/d3_tooltip';

export default {
  name: 'AppChartGaugeSemiCircle',
  props: {
    arcs: {
      type: Array,
      required: true,
      validator: (val) => val.length >= 1 && val.length <= 2
        && val.every((a) => typeof a.value === 'number' && a.value >= 0 && a.value <= 200),
    },
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    thickness: {
      type: Number,
      default: 18,
    },
  },
  data: () => ({
    tooltips: [],
    tooltipOptions: Object.freeze({
      placement: 'top',
      trigger: 'manual',
      html: true,
      sanitize: false,
      template:
        '<div class="vue-tooltip chart-gauge-tooltip top"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
    }),
  }),
  computed: {
    outerArc() {
      return this.arcs[0];
    },
    innerArc() {
      return this.arcs.length > 1 ? this.arcs[1] : null;
    },
    viewBoxWidth() {
      return this.width;
    },
    viewBoxHeight() {
      return this.height || this.viewBoxWidth / 2;
    },
    gap() {
      return this.thickness + 22;
    },
    outerRx() {
      return this.viewBoxWidth / 2 - this.thickness - 5;
    },
    outerRy() {
      return this.viewBoxHeight - this.thickness - 5;
    },
    innerRx() {
      return this.outerRx - this.gap;
    },
    innerRy() {
      return this.outerRy - this.gap;
    },
    outerCorrection() {
      return this.getCorrectionPercentage(this.outerRx, this.outerRy);
    },
    innerCorrection() {
      return this.getCorrectionPercentage(this.innerRx, this.innerRy);
    },
    markers() {
      return [25, 50, 75].map((p) => {
        const angle = 270 + (p / 100) * 180;
        const innerMargin = this.innerArc ? 60 : this.thickness / 2 + 6;
        const outerMargin = this.innerArc ? 15 : this.thickness / 2 + 6;

        const innerPoint = this.polarToCartesian(0, 0, this.outerRx - innerMargin, this.outerRy - innerMargin, angle);
        const outerPoint = this.polarToCartesian(0, 0, this.outerRx + outerMargin, this.outerRy + outerMargin, angle);
        const labelPoint = this.polarToCartesian(0, 0, this.outerRx + 26, this.outerRy + 24, angle);

        return {
          percentage: p,
          line: {
            x1: innerPoint.x,
            y1: innerPoint.y,
            x2: outerPoint.x,
            y2: outerPoint.y,
          },
          label: {
            x: labelPoint.x,
            y: labelPoint.y - 5,
          },
        };
      });
    },
  },
  beforeUnmount() {
    this.cleanupTooltips();
  },
  methods: {
    polarToCartesian(centerX, centerY, radiusX, radiusY, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

      return {
        x: centerX + radiusX * Math.cos(angleInRadians),
        y: centerY + radiusY * Math.sin(angleInRadians),
      };
    },
    describeArc(x, y, radiusX, radiusY, startAngle, endAngle) {
      const start = this.polarToCartesian(x, y, radiusX, radiusY, startAngle);
      const end = this.polarToCartesian(x, y, radiusX, radiusY, endAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

      return [
        'M', start.x, start.y,
        'A', radiusX, radiusY, 0, largeArcFlag, 1, end.x, end.y,
      ].join(' ');
    },
    getCorrectionPercentage(radiusX, radiusY) {
      // Ramanujan approximation for semi-perimeter of ellipse
      // p ≈ π * (3(a+b) - sqrt((3a+b)(a+3b)))
      // But simple approximation is π * sqrt((a^2 + b^2) / 2)
      // We only need semi-perimeter.
      const a = radiusX;
      const b = radiusY;
      const semiPerimeter = Math.PI * Math.sqrt((a * a + b * b) / 2);
      
      return ((this.thickness / 3) / semiPerimeter) * 100;
    },
    getEndAngle(value, correction) {
      const correctedValue = Math.max(0, value - correction);

      return 270 + (correctedValue / 100) * 180;
    },
    shouldShowCap(value) {
      return value > 0;
    },
    getCapPos(radiusX, radiusY, value, correction) {
      const angle = this.getEndAngle(value, correction);

      return this.polarToCartesian(0, 0, radiusX, radiusY, angle);
    },
    getTooltipAnchorPos(radiusX, radiusY) {
      // Position at 360 degrees (top center)
      // Offset x by 3px to center the tooltip visually
      return { x: 2, y: -radiusY - 13 };
    },
    showTooltip(targetRef, content) {
      if (!content) {return;}

      const target = this.$refs[targetRef];

      if (!target) {return;}

      this.cleanupTooltips();
      const tooltip = new D3Tooltip(target, content, this.tooltipOptions);

      tooltip.show();
      this.tooltips.push(tooltip);
    },
    cleanupTooltips() {
      this.tooltips.forEach((tooltip) => tooltip.dispose());
      this.tooltips = [];
    },
  },
};
</script>
